import { useRef, useCallback, useEffect, useMemo } from 'react';
import styles from './FJVideoRemovePreviewBox.module.less';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useState } from 'react';
import FJAddIcon from '../../../../assets/svg/add-point.svg?react';
import FJRemoveIcon from '../../../../assets/svg/remove-point.svg?react';
import FJWarnIcon from '../../../../assets/svg/warn1.svg?react';
import FJReloadIcon from '../../../../assets/svg/reload.svg?react';
import FJDeleteIcon from '../../../../assets/svg/delete.svg?react';
import FJUtil from '../../../../util/FJUtil';
import FJLoading from '../../../../common/components/FJLoading/FJLoading';
import FJVideoRemoveBgAfterControlsContainer from '../container/FJVideoRemoveBgAfterControlsContainer';
import FJEvent from '../../../../store/FJEvent';
import { FJ_EVENT_TYPE } from '../../../../constants/FJEvenTypeConstant';
import { ID_LIST } from '../../../../redux/slices/FJSliceVideoBgRemover';

const FJVideoRemovePreviewBox = ({
    editVideoInfo,
    addPoint,
    setCanvas,
    tracklets,
    currentTime,
    deletePoint,
    retryAddPoint,
    deleteErrorPoint,
    hasPointLoading,
    isRemoveBgAfter = false,
    isAutoRemoveBg = false,
    isPlay,
    pauseVideo,
    playVideo,
    disabled = false,
}) => {
    const canvasRef = useRef(null);
    const videoContentRef = useRef(null);
    const videoPreviewBoxRef = useRef(null);
    const [errorPointBtuShowList, setErrorPointBtuShowList] = useState([]);
    const [isFullScreen, setIsFullScreen] = useState(false);

    const handleFullScreenChange = useCallback(() => {
        const isFull = document.fullscreenElement != null;
        setIsFullScreen(isFull);
    }, []);

    useEffect(() => {
        document.addEventListener('fullscreenchange', handleFullScreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullScreenChange);
        };
    }, [handleFullScreenChange]);

    useEffect(() => {
        if (tracklets) {
            setErrorPointBtuShowList(...tracklets.map(tracklet => tracklet.points.map(() => false)));
        }
    }, [tracklets]);

    const errorPointBtuShowHandler = useCallback(pointIndex => {
        setErrorPointBtuShowList(prev => {
            const newList = [...prev];
            newList[pointIndex] = !prev[pointIndex];
            return newList;
        });
    }, []);

    const hideErrorPointBtu = useCallback(() => {
        setErrorPointBtuShowList(prev => prev && prev.length > 0 && prev.map(() => false));
    }, []);

    useEffect(() => {
        window.addEventListener('mousedown', hideErrorPointBtu);
        return () => {
            window.removeEventListener('mousedown', hideErrorPointBtu);
        };
    }, [hideErrorPointBtu]);

    const [videoCanvasRect, setVideoCanvasRect] = useState({ width: 0, height: 0 });
    const [videoContentRect, setVideoContentRect] = useState({ width: 0, height: 0 });
    const [fullCanvasRect, setFullCanvasRect] = useState({ width: 0, height: 0 });

    const mouseDownHandler = useCallback(
        e => {
            if (disabled || hasPointLoading || isRemoveBgAfter || isAutoRemoveBg) return;
            const videoContentRect = videoPreviewBoxRef.current.getBoundingClientRect();
            const point = {
                x: (e.clientX - videoContentRect.left) / videoContentRect.width,
                y: (e.clientY - videoContentRect.top) / videoContentRect.height,
            };

            addPoint && addPoint(point);
        },
        [addPoint, hasPointLoading, isRemoveBgAfter, isAutoRemoveBg, disabled],
    );

    const updateCanvasRect = useCallback(() => {
        if (!editVideoInfo || !videoContentRef.current) return {};

        const videoContentRect = videoContentRef.current.getBoundingClientRect();
        if (videoContentRect.width === 0 || videoContentRect.height === 0) return {};

        const aspectRatio = editVideoInfo.width / editVideoInfo.height;
        const containerAspectRatio = videoContentRect.width / videoContentRect.height;
        let width, height;

        if (aspectRatio > containerAspectRatio) {
            width = videoContentRect.width;
            height = width / aspectRatio;
        } else {
            height = videoContentRect.height;
            width = height * aspectRatio;
        }
        setVideoContentRect({ width: videoContentRect.width, height: videoContentRect.height });
        setVideoCanvasRect({ width, height });
    }, [videoContentRef, editVideoInfo]);

    useEffect(() => {
        if (setCanvas) {
            setCanvas(canvasRef.current);
        }
    }, [setCanvas]);

    useEffect(() => {
        updateCanvasRect();
        window.addEventListener('resize', updateCanvasRect);
        return () => window.removeEventListener('resize', updateCanvasRect);
    }, [updateCanvasRect]);

    useEffect(() => {
        if (isFullScreen) {
            // 获取屏幕分辨率
            const screenWidth = window.screen.width;
            const screenHeight = window.screen.height;
            const aspectRatio = editVideoInfo.width / editVideoInfo.height;
            let width, height;

            if (aspectRatio > 1) {
                width = screenWidth;
                height = width / aspectRatio;
            } else {
                height = screenHeight;
                width = height * aspectRatio;
            }

            if (width > screenWidth) {
                width = screenWidth;
                height = width / aspectRatio;
            }

            if (height > screenHeight) {
                height = screenHeight;
                width = height * aspectRatio;
            }

            setFullCanvasRect({
                width,
                height,
                left: (screenWidth - width) / 2,
                top: (screenHeight - height) / 2,
            });
        }
    }, [isFullScreen, editVideoInfo]);

    const checkFullScreen = () => {
        let isFull =
            document.webkitIsFullScreen ||
            document.mozFullScreen ||
            document.msFullscreenElement ||
            document.fullscreenElement;
        if (isFull == null || isFull == undefined) {
            isFull = false;
        }
        return isFull;
    };

    const _fullScreenClick = useCallback(() => {
        if (disabled) return;
        FJEvent.dispatch(FJ_EVENT_TYPE.HIDE_SIDE_MODAL);
        if (checkFullScreen()) {
            setIsFullScreen(false);
            document.exitFullscreen();
        } else {
            videoPreviewBoxRef.current.requestFullscreen();
            setIsFullScreen(true);
        }
    }, [disabled]);

    const _onClick = useCallback(() => {
        if (disabled) return;
        if (isRemoveBgAfter || isAutoRemoveBg) {
            FJEvent.dispatch(FJ_EVENT_TYPE.HIDE_SIDE_MODAL);

            if (isPlay) {
                pauseVideo && pauseVideo();
            } else {
                playVideo && playVideo();
            }
        }
    }, [isRemoveBgAfter, isAutoRemoveBg, pauseVideo, playVideo, isPlay, disabled]);

    const isShowControl = useMemo(() => {
        return isRemoveBgAfter || isAutoRemoveBg;
    }, [isRemoveBgAfter, isAutoRemoveBg]);
    return (
        <div
            className={classNames(styles.app_right_preview, {
                [styles.active]: editVideoInfo,
                [styles.not_show_control]: !isShowControl,
            })}
        >
            <div className={styles.video_container} ref={videoContentRef}>
                <div
                    className={classNames(styles.video_preview_box, {
                        [styles.full_screen]: isFullScreen,
                    })}
                    style={{
                        width: videoCanvasRect.width,
                        height: videoCanvasRect.height,
                    }}
                    ref={videoPreviewBoxRef}
                    onClick={_onClick}
                    onMouseDown={mouseDownHandler}
                >
                    <canvas
                        className={styles.canvas}
                        style={
                            isFullScreen
                                ? {
                                      width: fullCanvasRect.width,
                                      height: fullCanvasRect.height,
                                      left: fullCanvasRect.left,
                                      top: fullCanvasRect.top,
                                  }
                                : null
                        }
                        ref={canvasRef}
                        id={ID_LIST.REMOVE_BG_CANVAS}
                    />

                    {isShowControl && (
                        <FJVideoRemoveBgAfterControlsContainer
                            fullScreenClick={_fullScreenClick}
                            isFullScreen={isFullScreen}
                            disabled={disabled}
                        />
                    )}
                </div>

                {tracklets &&
                    tracklets.map(tracklet => {
                        const points = tracklet.points;

                        if (!FJUtil.compareTimes(tracklet.currentTime, currentTime)) return null;

                        return points.map((point, index) => {
                            const x = point[0];
                            const y = point[1];
                            const type = point[2];
                            const offsetX = (videoContentRect.width - videoCanvasRect.width) / 2;
                            const offsetY = (videoContentRect.height - videoCanvasRect.height) / 2;

                            switch (type) {
                                case 1:
                                    return (
                                        <div
                                            key={index}
                                            className={classNames(styles.point_item, {
                                                [styles.disabled]: hasPointLoading,
                                            })}
                                            style={{
                                                left: x * videoCanvasRect.width + offsetX,
                                                top: y * videoCanvasRect.height + offsetY,
                                            }}
                                            onClick={() => !hasPointLoading && deletePoint(index)}
                                            onMouseDown={e => {
                                                e.stopPropagation();
                                            }}
                                        >
                                            <FJAddIcon className={styles.add_icon} />
                                            <FJRemoveIcon className={styles.remove_icon} />
                                        </div>
                                    );
                                case 2:
                                case 3:
                                    return (
                                        <div
                                            key={index}
                                            style={{
                                                left: x * videoCanvasRect.width + offsetX,
                                                top: y * videoCanvasRect.height + offsetY,
                                                zIndex: errorPointBtuShowList[index] ? 1 : null,
                                            }}
                                            className={classNames(styles.point_item, {
                                                [styles.disabled]: hasPointLoading,
                                            })}
                                            onMouseDown={e => {
                                                e.stopPropagation();
                                                errorPointBtuShowHandler(index);
                                            }}
                                        >
                                            <FJWarnIcon className={styles.warn_icon} />

                                            <div
                                                className={classNames(styles.btu_box, {
                                                    [styles.show]: errorPointBtuShowList[index],
                                                })}
                                                onMouseDown={e => {
                                                    e.stopPropagation();
                                                }}
                                            >
                                                <div
                                                    className={styles.btu_item}
                                                    onClick={e => {
                                                        e.stopPropagation();

                                                        !hasPointLoading && retryAddPoint(index);
                                                    }}
                                                >
                                                    <FJReloadIcon />
                                                </div>
                                                <div
                                                    className={styles.btu_item}
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        hideErrorPointBtu();
                                                        !hasPointLoading && deleteErrorPoint(index);
                                                    }}
                                                >
                                                    <FJDeleteIcon />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                case 4:
                                    return (
                                        <div
                                            key={index}
                                            className={styles.loading}
                                            style={{
                                                left: x * videoCanvasRect.width + offsetX,
                                                top: y * videoCanvasRect.height + offsetY,
                                            }}
                                        >
                                            <FJLoading />
                                        </div>
                                    );
                                default:
                                    return null;
                            }
                        });
                    })}
            </div>
        </div>
    );
};

FJVideoRemovePreviewBox.propTypes = {
    editVideoInfo: PropTypes.object,
    addPoint: PropTypes.func,
    setCanvas: PropTypes.func,
    tracklets: PropTypes.array,
    currentTime: PropTypes.number,
    deletePoint: PropTypes.func,
    retryAddPoint: PropTypes.func,
    deleteErrorPoint: PropTypes.func,
    hasPointLoading: PropTypes.bool,
    isRemoveBgAfter: PropTypes.bool,
    pauseVideo: PropTypes.func,
    isAutoRemoveBg: PropTypes.bool,
    isPlay: PropTypes.bool,
    playVideo: PropTypes.func,
    disabled: PropTypes.bool,
};

export default FJVideoRemovePreviewBox;
